import {api} from "@/lib/api";
import dayjs from "dayjs";
import {message} from "ant-design-vue";
import {Brand} from "@/model/brand";
import {Employee} from "@/model/employee";
import {CustomerPhone, ICustomerPhone} from "@/model/customer_phones";

export interface IBranch {
    branch_id: string;
    brand_id: string;
    name: string;
    province: string;
    city: string;
    district: string;
    address: string;
    coordinate: number[];
    tel: string;
    wx_account_qr_code: string | null;
    subscribe_wx_open_id: string | null;
    trash_at: string | null;
}

export class Branch {
    constructor(readonly brand: Brand, readonly meta: IBranch) {
    }

    async update(br: IBranch): Promise<void> {
        if (br.branch_id !== this.meta.branch_id || br.brand_id !== this.meta.brand_id) return Promise.reject('ID 错误')
        return api({
            url: `user/${this.brand.meta.agent_id}/brand/${br.brand_id}/branch/${br.branch_id}`,
            method: 'PUT',
            data: br
        }).then(() => {
            Object.assign(this.meta, br)
        })
    }

    async remove(): Promise<void> {
        const done = message.loading('移除中', 0)
        return api({
            url: `user/${this.brand.meta.agent_id}/brand/${this.brand.meta.brand_id}/branch/${this.meta.branch_id}`,
            method: 'DELETE',
        }).then(() => {
            this.meta.trash_at = dayjs().format('YYYY-MM-DD HH:mm:ss');
        }).finally(done)
    }

    async restore(): Promise<void> {
        const done = message.loading('恢复中', 0)
        return api({
            url: `user/${this.brand.meta.agent_id}/brand/${this.brand.meta.brand_id}/branch/${this.meta.branch_id}`,
            method: 'PATCH'
        }).then(() => {
            this.meta.trash_at = null
        }).finally(done)
    }

    async employees(): Promise<Employee[]> {
        return this.brand.userAgent.loadEmployees([this.meta.branch_id]).then(es => es.map(e => new Employee(this, e))) ?? []
    }

    async customerPhones(): Promise<CustomerPhone[]> {
        return this.brand.userAgent.loadCustomerPhones([this.meta.branch_id]).then(es => es.map(e => new CustomerPhone(this, e))) ?? []
    }

    async regAppletQR(): Promise<Blob> {
        return api({
            url: `user/${this.brand.meta.agent_id}/brand/${this.brand.meta.brand_id}/branch/${this.meta.branch_id}/employee/reg/qr`,
            responseType: 'blob'
        })
    }

    async customerPhonesImport(pns: string[], isNew: boolean): Promise<ICustomerPhone[]> {
        return api<void, ICustomerPhone[] | null>({
            url: `user/${this.brand.meta.agent_id}/brand/${this.brand.meta.brand_id}/branch/${this.meta.branch_id}/customer/phones/import`,
            method: 'PUT',
            data: {phones: pns, is_new: isNew}
        }).then(this.onCustomerPhonesImport)
    }

    async customerPhonesImportXlsx(file: File, isNew: boolean): Promise<ICustomerPhone[]> {
        const fd = new FormData()
        fd.append('file', file)
        fd.append('is_new', isNew ? 't' : 'f')
        return api<void, ICustomerPhone[] | null>({
            url: `user/${this.brand.meta.agent_id}/brand/${this.brand.meta.brand_id}/branch/${this.meta.branch_id}/customer/phones/import/xlsx`,
            method: 'PUT',
            data: fd, headers: {'Content-Type': 'multipart/form-data'}
        }).then(this.onCustomerPhonesImport)
    }

    private onCustomerPhonesImport(cps: ICustomerPhone[] | null): ICustomerPhone[] {
        message.success(`成功导入 ${(cps ?? []).length} 条手机号`).then()
        if (!cps) return []
        if (this.brand.userAgent.meta.customerPhones.length == 0) this.brand.userAgent.meta.customerPhones = cps
        else {
            const ids = this.brand.userAgent.meta.customerPhones.map(c => c.customer_phone_id)
            cps.forEach(c => {
                if (ids.includes(c.customer_phone_id)) this.brand.userAgent.meta.customerPhones.splice(this.brand.userAgent.meta.customerPhones.findIndex(cp => cp.customer_phone_id == c.customer_phone_id), 1, c)
                else this.brand.userAgent.meta.customerPhones.push(c)
            })
        }
        return cps
    }
}